import { useQuery } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, Check, ImageIcon, LoaderCircle, Search, Star, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAdminApi } from '@app/api/api-client-context'
import type { ExerciseMediaAttachment } from '@entities/exercise/model'
import { normalizeExerciseMedia } from '@entities/exercise/model'
import { mediaKeys, mediaKindLabel, type MediaAsset } from '@entities/media'
import { unwrapApiResponse } from '@shared/api'

const previewUrl = (asset: MediaAsset) => asset.variants?.find(variant => variant.role === 'main')?.url ?? asset.variants?.[0]?.url
const commonRoles = ['cover', 'content', 'thumbnail', 'demo']

export const ExerciseMediaPicker = ({
	value,
	onChange,
	disabled,
}: {
	value: ExerciseMediaAttachment[]
	onChange: (value: ExerciseMediaAttachment[]) => void
	disabled?: boolean
}) => {
	const api = useAdminApi()
	const [search, setSearch] = useState('')
	const query = useQuery({
		queryKey: mediaKeys.list({ state: 'READY', search, limit: 100, surface: 'exercise-picker' }),
		queryFn: async () => unwrapApiResponse(await api.GET('/admin/v1/media', {
			params: { query: { state: ['READY'], search: search.trim() || undefined, limit: 100 } },
		})).data,
		staleTime: 15_000,
	})
	const assets = query.data?.items ?? []
	const assetByID = useMemo(() => new Map(assets.map(asset => [asset.id, asset])), [assets])
	const selected = value.map(attachment => ({ attachment, asset: assetByID.get(attachment.asset_id) }))
	const selectedIDs = useMemo(() => new Set(value.map(item => item.asset_id)), [value])
	const commit = (next: ExerciseMediaAttachment[]) => onChange(normalizeExerciseMedia(next))
	const add = (id: string) => {
		if (disabled || selectedIDs.has(id)) return
		commit([...value, {
			asset_id: id,
			role: value.length ? 'content' : 'cover',
			is_primary: value.length === 0,
			order: value.length,
			alt_text: undefined,
		}])
	}
	const remove = (id: string) => {
		if (!disabled) commit(value.filter(item => item.asset_id !== id))
	}
	const move = (index: number, direction: -1 | 1) => {
		if (disabled) return
		const nextIndex = index + direction
		if (nextIndex < 0 || nextIndex >= value.length) return
		const next = [...value]
		;[next[index], next[nextIndex]] = [next[nextIndex], next[index]]
		commit(next)
	}
	const patch = (id: string, patchValue: Partial<ExerciseMediaAttachment>) => {
		if (disabled) return
		let next = value.map(item => item.asset_id === id ? { ...item, ...patchValue } : item)
		if (patchValue.is_primary) next = next.map(item => ({ ...item, is_primary: item.asset_id === id }))
		commit(next)
	}

	return (
		<div className='grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]'>
			<div className='rounded-xl border p-4'>
				<div className='relative mb-4'>
					<Search className='pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
					<Input value={search} onChange={event => setSearch(event.target.value)} placeholder='Найти готовый медиафайл' className='pl-9' disabled={disabled} />
				</div>
				{query.isPending ? <div className='flex items-center gap-2 py-8 text-sm text-muted-foreground'><LoaderCircle className='h-4 w-4 animate-spin' /> Загрузка медиатеки…</div> : null}
				{query.isError ? <p role='alert' className='rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800'>Не удалось загрузить медиатеку.</p> : null}
				{query.data && !assets.length ? <p className='py-8 text-center text-sm text-muted-foreground'>Готовые медиафайлы не найдены.</p> : null}
				<div className='grid gap-2 sm:grid-cols-2'>
					{assets.map(asset => {
						const chosen = selectedIDs.has(asset.id)
						const preview = previewUrl(asset)
						return <button key={asset.id} type='button' disabled={disabled || chosen} onClick={() => add(asset.id)} className='flex min-w-0 items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted disabled:cursor-default disabled:opacity-70'>
							{preview && asset.kind !== 'VIDEO' ? <img src={preview} alt='' className='h-12 w-12 rounded-lg border object-cover' /> : <span className='flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border bg-muted'><ImageIcon className='h-5 w-5' /></span>}
							<span className='min-w-0 flex-1'><span className='block truncate text-sm font-medium'>{asset.filename}</span><span className='block text-xs text-muted-foreground'>{mediaKindLabel[asset.kind]} · {asset.variants?.length ?? 0} вариантов</span></span>
							{chosen ? <Check className='h-4 w-4 text-emerald-600' /> : null}
						</button>
					})}
				</div>
			</div>
			<div className='rounded-xl border p-4'>
				<div className='mb-3 flex items-center justify-between'><h4 className='font-medium'>Прикреплено</h4><span className='text-xs text-muted-foreground'>{value.length}</span></div>
				{!selected.length ? <p className='py-8 text-center text-sm text-muted-foreground'>Ассеты не выбраны. Первый добавленный ассет станет primary.</p> : null}
				<div className='space-y-3'>
					{selected.map(({ attachment, asset }, index) => {
						const roleOptions = commonRoles.includes(attachment.role) ? commonRoles : [attachment.role, ...commonRoles]
						return <div key={attachment.asset_id} className='space-y-3 rounded-lg border p-3'>
							<div className='flex items-center gap-2'>
								{asset && previewUrl(asset) && asset.kind !== 'VIDEO' ? <img src={previewUrl(asset)} alt='' className='h-10 w-10 rounded-md border object-cover' /> : <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-muted'><ImageIcon className='h-4 w-4' /></span>}
								<div className='min-w-0 flex-1'><p className='truncate text-sm font-medium'>{asset?.filename ?? attachment.asset_id}</p><p className='text-xs text-muted-foreground'>{asset ? mediaKindLabel[asset.kind] : 'Ассет вне текущей страницы поиска'}</p></div>
								{attachment.is_primary ? <span title='Primary asset' className='text-amber-600'><Star className='h-4 w-4 fill-current' /></span> : null}
								<div className='flex gap-1'>
									<Button type='button' size='icon' variant='ghost' disabled={disabled || index === 0} onClick={() => move(index, -1)} aria-label='Переместить выше'><ArrowUp className='h-4 w-4' /></Button>
									<Button type='button' size='icon' variant='ghost' disabled={disabled || index === value.length - 1} onClick={() => move(index, 1)} aria-label='Переместить ниже'><ArrowDown className='h-4 w-4' /></Button>
									<Button type='button' size='icon' variant='ghost' disabled={disabled} onClick={() => remove(attachment.asset_id)} aria-label='Отсоединить'><X className='h-4 w-4 text-red-600' /></Button>
								</div>
							</div>
							<div className='grid gap-3 sm:grid-cols-2'>
								<label className='text-xs font-medium'>Роль
									<select value={attachment.role} onChange={event => patch(attachment.asset_id, { role: event.target.value })} disabled={disabled} className='mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm'>{roleOptions.map(role => <option key={role} value={role}>{role}</option>)}</select>
								</label>
								<label className='flex items-end gap-2 pb-2 text-xs font-medium'><input type='radio' name='exercise-primary-media' checked={attachment.is_primary} onChange={() => patch(attachment.asset_id, { is_primary: true })} disabled={disabled} className='h-4 w-4 accent-primary' /> Primary</label>
							</div>
							<label className='block text-xs font-medium'>Alt text
								<Input value={attachment.alt_text ?? ''} onChange={event => patch(attachment.asset_id, { alt_text: event.target.value })} disabled={disabled} placeholder='Текстовое описание содержимого' className='mt-1' />
							</label>
						</div>
					})}
				</div>
			</div>
		</div>
	)
}
